/* =========================================================
   Shain Pro - Premium product card behaviour
   Add to cart, quick view and save for later for every
   product-card-ultra on the page. Uses delegated listeners so
   cards rendered later (AJAX filtering, load more, theme editor)
   work without re-initialising. Safe to load more than once.
   ========================================================= */
(function () {
  'use strict';

  if (window.ShainProCardReady) return;
  window.ShainProCardReady = true;

  var config = window.ShainProCard || {};
  var STRINGS = config.strings || {};
  var WISHLIST_KEY = 'shain-pro:wishlist';
  var toastEl = null;
  var toastTimer = null;
  var modal = null;
  var modalBody = null;
  var lastFocused = null;

  function text(key, fallback) {
    return STRINGS[key] || fallback || '';
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function cartAddUrl() {
    var routes = (window.ShainPro && window.ShainPro.routes) || {};
    return (routes.cartAdd || '/cart/add') + '.js';
  }

  function formatMoney(cents) {
    var amount = (Number(cents) || 0) / 100;
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: currency
      }).format(amount);
    } catch (error) {
      return amount.toFixed(2);
    }
  }

  /* ---------------- Toast ---------------- */
  function showToast(message, scope) {
    if (!message) return;
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.className = 'pcu-toast ' + scopeClasses(scope);
    toastEl.textContent = message;
    window.clearTimeout(toastTimer);
    window.requestAnimationFrame(function () {
      toastEl.classList.add('is-visible');
    });
    toastTimer = window.setTimeout(function () {
      toastEl.classList.remove('is-visible');
    }, 2600);
  }

  function scopeClasses(element) {
    var scope = element && element.closest ? element.closest('.pcu') : null;
    if (!scope) return 'pcu';
    return scope.className
      .split(/\s+/)
      .filter(function (name) {
        return name === 'pcu' || name === 'pcu--d1' || name === 'pcu--d2' || name.indexOf('pcu--btn-') === 0;
      })
      .join(' ');
  }

  /* ---------------- Cart ---------------- */
  function afterAdd(scope) {
    var cartApi = window.ShainProCart;
    var drawer = document.getElementById('CartDrawer');
    var drawers = window.ShainPro && window.ShainPro.Drawers;
    var refresh = cartApi && typeof cartApi.refresh === 'function' ? cartApi.refresh() : Promise.resolve();

    return Promise.resolve(refresh)
      .catch(function () {
        return null;
      })
      .then(function () {
        if (config.afterAdd !== 'toast' && drawer && drawers && typeof drawers.open === 'function') {
          drawers.open(drawer);
        } else {
          showToast(text('added', 'Added to cart'), scope);
        }
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
      });
  }

  function addToCart(formData, button) {
    if (button) {
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
    }

    return fetch(cartAddUrl(), {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData
    })
      .then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) throw new Error(data.description || data.message || text('error'));
          return data;
        });
      })
      .then(function (data) {
        return afterAdd(button).then(function () {
          return data;
        });
      })
      .catch(function (error) {
        showToast(error && error.message ? error.message : text('error'), button);
        throw error;
      })
      .finally(function () {
        if (button) {
          button.classList.remove('is-loading');
          button.removeAttribute('aria-busy');
          button.disabled = false;
        }
      });
  }

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.classList.contains('fcu__form')) return;
    if (!form.closest('.pcu')) return;

    event.preventDefault();
    addToCart(new FormData(form), form.querySelector('[data-fcu-add]')).catch(function () {});
  });

  /* ---------------- Save for later ---------------- */
  function readWishlist() {
    try {
      var raw = window.localStorage.getItem(WISHLIST_KEY);
      var parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  function writeWishlist(list) {
    try {
      window.localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
    } catch (error) {
      /* Storage unavailable - the choice is kept for this page view only. */
    }
  }

  function syncWishlist(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var saved = readWishlist();
    scope.querySelectorAll('[data-fcu-wishlist]').forEach(function (button) {
      button.setAttribute('aria-pressed', saved.indexOf(button.dataset.handle) !== -1 ? 'true' : 'false');
    });
  }

  function toggleWishlist(button) {
    var handle = button.dataset.handle;
    if (!handle) return;

    var list = readWishlist();
    var index = list.indexOf(handle);
    var saved = index === -1;

    if (saved) {
      list.push(handle);
    } else {
      list.splice(index, 1);
    }
    writeWishlist(list);

    document.querySelectorAll('[data-fcu-wishlist]').forEach(function (other) {
      if (other.dataset.handle === handle) other.setAttribute('aria-pressed', saved ? 'true' : 'false');
    });
    showToast(saved ? text('saved') : text('removed'), button);
  }

  /* ---------------- Quick view ---------------- */
  function ensureModal() {
    if (modal) return modal;
    if (typeof HTMLDialogElement !== 'function') return null;

    modal = document.createElement('dialog');
    modal.className = 'pcu-modal pcu';
    modal.setAttribute('aria-label', text('quickView', 'Quick view'));
    modal.innerHTML =
      '<button type="button" class="fcu__modal-close" data-pcu-modal-close aria-label="' + escapeHtml(text('close', 'Close')) + '">' +
      '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M18 6 6 18M6 6l12 12"/></svg>' +
      '</button><div class="fcu__modal-inner" data-pcu-modal-body></div>';
    document.body.appendChild(modal);
    modalBody = modal.querySelector('[data-pcu-modal-body]');

    modal.addEventListener('click', function (event) {
      if (event.target === modal || (event.target.closest && event.target.closest('[data-pcu-modal-close]'))) {
        closeModal();
      }
    });
    modal.addEventListener('close', function () {
      if (modalBody) modalBody.innerHTML = '';
      var drawerOpen = document.body.classList.contains('drawer-open');
      if (!drawerOpen && lastFocused && typeof lastFocused.focus === 'function' && document.contains(lastFocused)) {
        lastFocused.focus();
      }
    });

    return modal;
  }

  function closeModal() {
    if (modal && modal.open) modal.close();
  }

  function renderQuickView(product, url) {
    var image = product.featured_image || (product.images && product.images[0]) || '';
    var variants = product.variants || [];
    var hasVariants = variants.length > 1;
    var firstAvailable = variants.filter(function (variant) { return variant.available; })[0] || variants[0];
    var available = firstAvailable ? firstAvailable.available : false;

    var options = '';
    if (hasVariants) {
      options =
        '<select class="fcu__select" data-pcu-variant aria-label="' + escapeHtml(text('chooseVariant', 'Choose a variant')) + '">' +
        variants
          .map(function (variant) {
            return (
              '<option value="' + variant.id + '"' +
              (firstAvailable && variant.id === firstAvailable.id ? ' selected' : '') +
              (variant.available ? '' : ' disabled') + '>' +
              escapeHtml(variant.title) + ' — ' + formatMoney(variant.price) +
              (variant.available ? '' : ' (' + escapeHtml(text('soldOut', 'Sold out')) + ')') +
              '</option>'
            );
          })
          .join('') +
        '</select>';
    }

    var compare =
      firstAvailable && firstAvailable.compare_at_price > firstAvailable.price
        ? '<s class="fcu__price--compare" data-pcu-modal-compare>' + formatMoney(firstAvailable.compare_at_price) + '</s>'
        : '<s class="fcu__price--compare" data-pcu-modal-compare hidden></s>';

    modalBody.innerHTML =
      '<div class="fcu__modal-media">' + (image ? '<img src="' + escapeHtml(image) + '" alt="" loading="lazy">' : '') + '</div>' +
      '<div class="fcu__modal-content">' +
      (product.vendor ? '<p class="fcu__vendor">' + escapeHtml(product.vendor) + '</p>' : '') +
      '<h2 class="fcu__modal-title">' + escapeHtml(product.title) + '</h2>' +
      '<div class="fcu__prices"><span class="fcu__price" data-pcu-modal-price>' +
      formatMoney(firstAvailable ? firstAvailable.price : product.price) + '</span>' + compare + '</div>' +
      options +
      '<button type="button" class="fcu__btn" data-pcu-modal-add' + (available ? '' : ' disabled') + '>' +
      '<span class="fcu__spinner" aria-hidden="true"></span>' +
      '<span class="fcu__btn-label">' + escapeHtml(available ? text('addToCart', 'Add to cart') : text('soldOut', 'Sold out')) + '</span></button>' +
      '<a class="fcu__btn fcu__btn--ghost" href="' + escapeHtml(url) + '"><span class="fcu__btn-label">' + escapeHtml(text('viewDetails', 'View details')) + '</span></a>' +
      '<div class="fcu__modal-desc">' + (product.description || '') + '</div>' +
      '</div>';

    var select = modalBody.querySelector('[data-pcu-variant]');
    var priceEl = modalBody.querySelector('[data-pcu-modal-price]');
    var compareEl = modalBody.querySelector('[data-pcu-modal-compare]');
    var addButton = modalBody.querySelector('[data-pcu-modal-add]');

    if (select) {
      select.addEventListener('change', function () {
        var variant = variants.filter(function (item) { return String(item.id) === select.value; })[0];
        if (!variant) return;
        if (priceEl) priceEl.textContent = formatMoney(variant.price);
        if (compareEl) {
          var onSale = variant.compare_at_price > variant.price;
          compareEl.hidden = !onSale;
          compareEl.textContent = onSale ? formatMoney(variant.compare_at_price) : '';
        }
        if (addButton) {
          addButton.disabled = !variant.available;
          addButton.querySelector('.fcu__btn-label').textContent = variant.available ? text('addToCart', 'Add to cart') : text('soldOut', 'Sold out');
        }
      });
    }

    if (addButton) {
      addButton.addEventListener('click', function () {
        var variantId = select ? select.value : firstAvailable && firstAvailable.id;
        if (!variantId) return;
        var data = new FormData();
        data.append('id', variantId);
        data.append('quantity', '1');
        addToCart(data, addButton)
          .then(closeModal)
          .catch(function () {});
      });
    }
  }

  function openQuickView(trigger) {
    var url = trigger.dataset.url;
    if (!url) return;

    var dialog = ensureModal();
    if (!dialog || typeof dialog.showModal !== 'function') {
      window.location.href = url;
      return;
    }

    dialog.className = 'pcu-modal ' + scopeClasses(trigger);
    lastFocused = trigger;
    modalBody.innerHTML = '<div class="fcu__modal-content"><p class="fcu__modal-desc">' + escapeHtml(text('loading', 'Loading')) + '…</p></div>';
    if (!dialog.open) dialog.showModal();

    var productUrl = url.split('?')[0] + '.js';
    fetch(productUrl, { headers: { Accept: 'application/json' } })
      .then(function (response) {
        if (!response.ok) throw new Error('Request failed');
        return response.json();
      })
      .then(function (product) {
        if (dialog.open) renderQuickView(product, url);
      })
      .catch(function () {
        closeModal();
        window.location.href = url;
      });
  }

  /* ---------------- Delegated clicks ---------------- */
  document.addEventListener('click', function (event) {
    var target = event.target instanceof Element ? event.target : null;
    if (!target || !target.closest('.pcu')) return;

    var wishlist = target.closest('[data-fcu-wishlist]');
    if (wishlist) {
      event.preventDefault();
      toggleWishlist(wishlist);
      return;
    }

    var quickView = target.closest('[data-fcu-quickview]');
    if (quickView) {
      event.preventDefault();
      openQuickView(quickView);
    }
  });

  /* ---------------- Keep saved state in sync ---------------- */
  function onReady() {
    syncWishlist(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }

  ['collection:updated', 'shopify:section:load'].forEach(function (name) {
    document.addEventListener(name, function (event) {
      syncWishlist(event.target && event.target.querySelectorAll ? event.target : document);
    });
  });

  window.addEventListener('storage', function (event) {
    if (event.key === WISHLIST_KEY) syncWishlist(document);
  });
})();
